// "Add New Content" (e.g. Stories > Add New Story): one story/lesson saved as a chapter of the selected
// subcategory course, with PDF / Audio / Video files (from the content library "MKP" or a new upload),
// a game/activity link, a quiz, ISF coins per item, and extra coin rules (e.g. Expression = 10 coins).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Coins, Eye, ImagePlus, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import Swal from 'sweetalert2';
import { api } from '../../api';
import { useRBAC } from '../../contexts/RBACContext';
import GComprisRewardModal from '../../components/admin/GComprisRewardModal';
import LoadingState from '../../components/common/LoadingState';
import { confirmDialog, resultDialog } from '../../utils/swalDialogs';
import ContentDetailsDialog from './ContentDetailsDialog';
import {
  CategorySidebar,
  SubcategoryActions,
  canAddSubcategoryUnder,
  categoryIcon,
  categoryLabel,
  categoryMeta,
  contentItemNaming,
  contentRowsOf,
  courseForRow,
  coursesInRow,
  courseInSubcategory,
  findSubcategoryNode,
  isGComprisRow,
  promptAddCategory,
  promptAddSidebarSubcategory,
  subcategoryPath,
  useCourseCategories,
  useCourseSubcategories,
} from './coursesShared';
import SearchableSelect from '../../components/common/SearchableSelect';
import { QUIZ_PICKER_PARAMS, quizzesWithQuestions } from '../../utils/quizPicker';

const DESC_MAX = 500;
const UPLOAD_NEW = '__upload__';
const MAX_THUMB_BYTES = 2 * 1024 * 1024;
const coins = (v) => Math.max(0, Math.round(Number(v) || 0));

// Fixed rows, as in the design. `libraryType` picks the MKP and Content Library files offered for the row.
const ROWS = [
  { key: 'pdf', type: 'pdf', label: 'PDF – Story / Worksheet', icon: '📄', libraryType: 'pdf', accept: '.pdf' },
  { key: 'audio', type: 'audio', label: 'Audio – Story Narration', icon: '🎧', libraryType: 'audio', accept: 'audio/*' },
  { key: 'video', type: 'video', label: 'Video – Animated / Real', icon: '🎬', libraryType: 'video', accept: 'video/*' },
  { key: 'game', type: 'link', label: 'Game – Activity', icon: '🎮' },
  { key: 'quiz', type: 'quiz', label: 'Quiz – Questions', icon: '❓' },
];
const emptyRow = () => ({ title: '', fileUrl: '', externalUrl: '', quizRef: '', coins: 0 });
// Files of the MKP content app (GET .../courses/files/mkp), by type: { pdf: [...], audio: [...], video: [...] }.
const byFileType = (files = []) => files.reduce((all, f) => ({ ...all, [f.fileType]: [...(all[f.fileType] || []), f] }), {});
// [["Balgruha Content › Action Songs", files], ...] in MKP's own order, one <optgroup> each.
const mkpGroups = (files = []) => {
  const groups = new Map();
  files.forEach((f) => {
    const label = [f.parentCategory, f.category].filter(Boolean).join(' › ') || 'Other';
    groups.set(label, [...(groups.get(label) || []), f]);
  });
  return [...groups];
};
const mkpLabel = (f) => (f.language ? `${f.fileName} (${f.language})` : f.fileName);
// The first MKP read after a server start can take a while (backend services/mkpContent.js).
const MKP_TIMEOUT_MS = 90 * 1000;
// Shown (greyed out) in a picker without MKP files, by mkpStatus.
const MKP_NOTES = {
  loading: 'Loading MKP files…',
  off: 'MKP is not connected on this server',
  error: 'Could not load MKP files. Reload the page to try again.',
};
const mkpNote = (status, type) => MKP_NOTES[status] || `No MKP ${type === 'pdf' ? 'PDF' : type} files`;
// One content item (a story / scene / song: saved as a chapter). `title: null` = not typed yet, so
// it shows the item's automatic name ("Action Song 1") when the course's subcategory gives one.
const blankItem = () => ({
  title: null,
  description: '',
  thumb: { url: '', preview: '' },
  defaultCoins: 0,
  rows: Object.fromEntries(ROWS.map((r) => [r.key, emptyRow()])),
  coinRules: [],
});
const swalForm = { customClass: { container: 'ds-swal', popup: 'ds-swal__popup', confirmButton: 'ds-swal__confirm', cancelButton: 'ds-swal__cancel' }, buttonsStyling: false, reverseButtons: true, showCancelButton: true, focusConfirm: false };
const escapeAttr = (v) => String(v || '').replace(/[&"<>]/g, (c) => ({ '&': '&amp;', '"': '&quot;', '<': '&lt;', '>': '&gt;' }[c]));
const quizLabel = (q) => `${q.title}${q.status ? ` (${q.status})` : ''}`;
// The course list without thumbnails: this page only needs course names, and some thumbnails are
// base64 images of a few MB each (with them the list was ~15 MB and the page took seconds to load).
const loadCourses = () => api.get('/api/v2/lms/admin/courses', { params: { thumbnails: false } });
// "Create a new quiz" opens the quiz form in this tab. The form here is kept in the session meanwhile and
// restored when the quiz form sends the admin back (?fromQuiz=1, plus &quiz=<id> once a quiz was saved).
const QUIZ_DRAFT_KEY = 'addCourseContent.quizDraft';
const readQuizDraft = (search) => {
  if (!new URLSearchParams(search).get('fromQuiz')) return null;
  try {
    return JSON.parse(sessionStorage.getItem(QUIZ_DRAFT_KEY) || 'null');
  } catch {
    return null;
  }
};

export default function AddCourseContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, isLoading: rbacLoading, permissions } = useRBAC();
  const query = new URLSearchParams(location.search);
  const { categories, reload: reloadCategories } = useCourseCategories();
  const { nodesFor, reload: reloadSubcategories } = useCourseSubcategories();

  const [draft] = useState(() => readQuizDraft(location.search));
  const returnedQuizId = query.get('fromQuiz') ? query.get('quiz') || '' : '';
  // "Back" returns to the page that opened this form (router state `from`, kept through the quiz form
  // in the draft); opened any other way, it goes to Courses Management.
  const [backTo] = useState(() => {
    const from = location.state?.from ?? draft?.backTo;
    return typeof from === 'string' && from.startsWith('/admin/courses') ? from : '/admin/courses';
  });

  const [category, setCategory] = useState(draft?.category ?? (query.get('category') || ''));
  // A subcategory picked in the sidebar narrows the subcategory strip to its courses.
  const [subcategory, setSubcategory] = useState(draft?.subcategory ?? (query.get('sub') || ''));
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [courseId, setCourseId] = useState(draft?.courseId ?? (query.get('course') || ''));
  const [library, setLibrary] = useState({});
  const [mkp, setMkp] = useState({});
  // 'loading' | 'ready' | 'off' (not set up on the server) | 'error' (MKP couldn't be read).
  const [mkpStatus, setMkpStatus] = useState('loading');
  // The quizzes that can be linked: only ones with questions (utils/quizPicker.js).
  const [quizzes, setQuizzes] = useState([]);
  const [quizzesLoaded, setQuizzesLoaded] = useState(false);
  // The items being added (one form each; "+ Add <item>" adds another) and the one shown.
  const [items, setItems] = useState(() => {
    const kept = draft?.items?.length
      ? draft.items
      : [{ title: draft?.title ?? null, description: draft?.description, thumb: draft?.thumb, defaultCoins: draft?.defaultCoins, rows: draft?.rows, coinRules: draft?.coinRules }];
    const at = Math.min(draft?.active || 0, kept.length - 1);
    return kept.map((keptItem, i) => {
      const base = blankItem();
      const rows = Object.fromEntries(ROWS.map((r) => [r.key, { ...emptyRow(), ...(keptItem.rows?.[r.key] || {}) }]));
      // The quiz just created in the quiz form; its title is filled in once the quiz list loads.
      if (returnedQuizId && i === at) rows.quiz = { ...rows.quiz, quizRef: returnedQuizId, title: '' };
      return {
        title: keptItem.title ?? base.title,
        description: keptItem.description || base.description,
        thumb: keptItem.thumb || base.thumb,
        defaultCoins: keptItem.defaultCoins ?? base.defaultCoins,
        rows,
        coinRules: keptItem.coinRules || base.coinRules,
      };
    });
  });
  const [active, setActive] = useState(() => Math.min(draft?.active || 0, Math.max((draft?.items?.length || 1) - 1, 0)));
  const { title, description, thumb, defaultCoins, rows, coinRules } = items[active] || items[0];
  // Setters for a field of the item shown (a function updates it from its current value).
  const setField = (key) => (value) =>
    setItems((list) => list.map((it, i) => (i === active ? { ...it, [key]: typeof value === 'function' ? value(it[key]) : value } : it)));
  const setTitle = setField('title');
  const setDescription = setField('description');
  const setThumb = setField('thumb');
  const setDefaultCoins = setField('defaultCoins');
  const setRows = setField('rows');
  const setCoinRules = setField('coinRules');
  const [busy, setBusy] = useState('');
  const [gcomprisOpen, setGcomprisOpen] = useState(false);
  const [details, setDetails] = useState(null); // a saved content opened from its tab
  const fileInputs = useRef({});
  const thumbInput = useRef(null);

  useEffect(() => {
    if (rbacLoading || !Object.keys(permissions).length) return;
    if (!hasPermission('LMS Management', 'Manage')) navigate('/access-denied');
  }, [hasPermission, navigate, rbacLoading, permissions]);

  useEffect(() => {
    loadCourses().then((r) => setCourses(r.data?.data || [])).catch(() => setCourses([])).finally(() => setCoursesLoading(false));
    api.get('/api/v2/lms/admin/quizzes', { params: { limit: 200, ...QUIZ_PICKER_PARAMS } })
      .then((r) => setQuizzes(quizzesWithQuestions(r.data?.quizzes)))
      .then(() => setQuizzesLoaded(true))
      .catch(() => setQuizzes([]));
    Promise.all(['pdf', 'audio', 'video'].map((t) => api.get('/api/v2/lms/admin/content/library', { params: { fileType: t, limit: 200 } }).catch(() => null)))
      .then(([pdf, audio, video]) => setLibrary({ pdf: pdf?.data?.files || [], audio: audio?.data?.files || [], video: video?.data?.files || [] }));
    api.get('/api/v2/lms/admin/courses/files/mkp', { timeout: MKP_TIMEOUT_MS })
      .then((r) => {
        setMkp(byFileType(r.data?.files));
        setMkpStatus(r.data?.configured === false ? 'off' : r.data?.error ? 'error' : 'ready');
      })
      .catch(() => {
        setMkp({});
        setMkpStatus('error');
      });
  }, []);

  // The kept form is used once; back from the quiz form, drop the return markers from the address.
  useEffect(() => {
    try { sessionStorage.removeItem(QUIZ_DRAFT_KEY); } catch { /* storage unavailable */ }
    if (!query.get('fromQuiz')) return;
    const clean = new URLSearchParams(location.search);
    clean.delete('fromQuiz');
    clean.delete('quiz');
    navigate({ search: clean.toString() ? `?${clean}` : '' }, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subcategoryNode = useMemo(() => findSubcategoryNode(nodesFor(category), subcategory), [nodesFor, category, subcategory]);
  const subcourses = useMemo(
    () => courses.filter((c) => c.category === category && c.status !== 'archived' && (!subcategoryNode || courseInSubcategory(c, subcategoryNode))),
    [courses, category, subcategoryNode]
  );
  // Admins add content to the row picked in the sidebar, not to a course: it goes into the course
  // placed right in that row (the oldest, if there are several; the list comes newest first), or
  // the one in the address (?course=, a course chip in Courses Management).
  const ownCourses = useMemo(
    () => subcourses.filter((c) => (subcategoryNode ? courseInSubcategory(c, { ...subcategoryNode, children: [] }) : !c.subcategory)).reverse(),
    [subcourses, subcategoryNode]
  );
  useEffect(() => {
    if (courses.length && courseId && !subcourses.some((c) => c._id === courseId)) setCourseId('');
  }, [subcourses]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickCategory = (name) => {
    if (!name) return;
    setCategory(name);
    setSubcategory('');
    setCourseId('');
  };
  // Picking the selected subcategory again goes back to the whole category.
  const pickSubcategory = (name, node) => {
    setSubcategory(name === category && node.key === subcategory ? '' : node.key);
    setCategory(name);
    setCourseId('');
  };

  const course = subcourses.find((c) => c._id === courseId) || ownCourses[0];
  const meta = categoryMeta(categories, category);
  const noun = /stor/i.test(category) ? 'Story' : 'Content';
  const where = [categoryLabel(category), subcategoryPath(nodesFor(category), course?.subcategory || subcategory)].filter(Boolean).join(' › ');
  // A last row (a subcategory with nothing under it, or a category without subcategories) with no
  // course yet: its form shows anyway, and Save first makes the course for it (courseForRow).
  const lastRowWithoutCourse = Boolean(category) && !coursesLoading && !course
    && (subcategoryNode ? !subcategoryNode.children.length : !subcategory && !nodesFor(category).length);
  const newCourse = useMemo(
    () => (lastRowWithoutCourse ? { ...courseForRow(category, subcategoryNode, where), modules: [], status: 'draft' } : null),
    [lastRowWithoutCourse, category, subcategoryNode, where]
  );
  // Items are named after the course's subcategory and numbered within the course ("Action Song 1",
  // "Action Song 2" ... in every course); each is saved with its own Syllabus Tracker number (naming.next).
  const naming = useMemo(() => {
    const target = course || newCourse;
    return contentItemNaming(nodesFor(target?.category || ''), courses, target);
  }, [nodesFor, courses, course, newCourse]);
  // The content the row already has (every course in it), shown before the new items.
  const savedRows = useMemo(() => contentRowsOf(coursesInRow(courses, course)), [courses, course]);
  const itemTitle = (i) => items[i]?.title ?? (naming ? `${naming.prefix} ${naming.first + i}` : '');
  const addItem = () => {
    setItems((list) => [...list, blankItem()]);
    setActive(items.length);
  };
  const removeItem = (i) => {
    setItems((list) => list.filter((_, j) => j !== i));
    setActive((at) => (at > i || (at === i && at === items.length - 1) ? Math.max(0, at - 1) : at));
  };
  const itemCoins = ROWS.reduce((n, r) => n + (isFilled(r, rows[r.key]) ? coins(rows[r.key].coins) : 0), 0);
  const ruleCoins = coinRules.reduce((n, r) => n + (r.label.trim() ? coins(r.coins) : 0), 0);
  const totalCoins = coins(defaultCoins) + itemCoins + ruleCoins;
  const dirty = items.some((it) => Boolean(it.title || it.description || it.thumb.url || it.coinRules.length || ROWS.some((r) => isFilled(r, it.rows[r.key]))));

  function isFilled(row, value) {
    if (row.type === 'link') return Boolean(value.externalUrl);
    if (row.type === 'quiz') return Boolean(value.quizRef);
    return Boolean(value.fileUrl);
  }
  const setRow = (key, patch) => setRows((all) => ({ ...all, [key]: { ...all[key], ...patch } }));

  useEffect(() => {
    if (!rows.quiz.quizRef || rows.quiz.title) return;
    const found = quizzes.find((q) => q._id === rows.quiz.quizRef);
    if (found) {
      setRow('quiz', { title: found.title });
      return;
    }
    // The quiz just made in the quiz form isn't one that can be linked: it has no questions yet.
    if (quizzesLoaded) {
      setRow('quiz', { quizRef: '', title: '' });
      resultDialog(
        'info',
        'This quiz has no questions yet',
        'Only a quiz with questions can be added to a course. Add questions to it in Quiz Management, then pick it here.'
      );
    }
  }, [quizzes, quizzesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    const form = new FormData();
    form.append('files', file);
    const res = await api.post('/api/v2/lms/admin/content/upload', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 10 * 60 * 1000 });
    const uploaded = res.data?.files?.[0];
    if (!uploaded?.fileUrl) throw new Error(res.data?.message || 'Upload failed');
    return uploaded;
  };

  const onPickFile = async (row, file) => {
    if (!file) return;
    setBusy(row.key);
    try {
      const uploaded = await upload(file);
      setRow(row.key, { fileUrl: uploaded.fileUrl, title: uploaded.fileName || file.name });
      setLibrary((lib) => ({ ...lib, [row.libraryType]: [uploaded, ...(lib[row.libraryType] || [])] }));
    } catch (err) {
      resultDialog('error', 'Upload failed', err?.response?.data?.error || err?.response?.data?.message || err.message);
    } finally {
      setBusy('');
    }
  };

  const onSource = (row, value) => {
    if (value === UPLOAD_NEW) { fileInputs.current[row.key]?.click(); return; }
    const picked = [...(mkp[row.libraryType] || []), ...(library[row.libraryType] || [])].find((f) => f.fileUrl === value);
    setRow(row.key, { fileUrl: value, title: picked?.fileName || rows[row.key].title });
  };

  const editGame = async () => {
    const cur = rows.game;
    const { value } = await Swal.fire({
      ...swalForm,
      title: 'Game / Activity',
      confirmButtonText: 'Save',
      html: `<div class="cm-swal-form">
        <label>Name<input id="cm-game-name" class="swal2-input" maxlength="200" placeholder="e.g. Match the characters" value="${escapeAttr(cur.title)}" /></label>
        <label>Game link (http/https)<input id="cm-game-url" class="swal2-input" placeholder="https://..." value="${escapeAttr(cur.externalUrl)}" /></label>
      </div>`,
      preConfirm: () => {
        const name = document.getElementById('cm-game-name').value.trim();
        const url = document.getElementById('cm-game-url').value.trim();
        if (!/^https?:\/\//i.test(url)) { Swal.showValidationMessage('Enter a link starting with http:// or https://'); return false; }
        return { name: name || 'Game / Activity', url };
      },
    });
    if (value) setRow('game', { title: value.name, externalUrl: value.url });
  };

  const fetchQuizzes = useCallback(async () => {
    try {
      const r = await api.get('/api/v2/lms/admin/quizzes', { params: { limit: 200, ...QUIZ_PICKER_PARAMS } });
      const list = quizzesWithQuestions(r.data?.quizzes);
      setQuizzes(list);
      setQuizzesLoaded(true);
      return list;
    } catch {
      return null;
    }
  }, []);

  // Opens Quiz Management's create-quiz form in this tab. This form is kept and comes back as it was,
  // with the new quiz selected, once the quiz is saved (or the quiz form is left with its back arrow).
  const goCreateQuiz = () => {
    if (busy) {
      resultDialog('error', 'Upload in progress', 'Wait for the upload to finish, then create the quiz.');
      return;
    }
    try {
      sessionStorage.setItem(QUIZ_DRAFT_KEY, JSON.stringify({ category, subcategory, courseId, title, description, thumb, defaultCoins, rows, coinRules, items, active, backTo }));
    } catch { /* storage unavailable: the form starts empty on return */ }
    const back = new URLSearchParams();
    if (category) back.set('category', category);
    if (subcategory) back.set('sub', subcategory);
    if (courseId) back.set('course', courseId);
    back.set('fromQuiz', '1');
    const backUrl = `/admin/courses/content/new?${back}`;
    // Mark this page's own history entry too, so the browser's Back button also brings the form back.
    navigate(backUrl, { replace: true });
    navigate(`/admin/quizzes/create?returnTo=${encodeURIComponent(backUrl)}`);
  };

  const editQuiz = async () => {
    // A quiz may have been created in another tab since the page loaded.
    let known = quizzes.length ? quizzes : (await fetchQuizzes()) || [];
    if (!known.length) {
      const go = await confirmDialog({
        title: 'No quizzes with questions yet',
        text: 'Only a quiz with questions can be added. Create one in Quiz Management. You will come back to this form as you left it, with the new quiz selected.',
        confirmText: 'Create a Quiz',
        cancelText: 'Stay here',
      });
      if (go) goCreateQuiz();
      return;
    }
    // A quiz opened in another tab (Ctrl/middle-click on the link) shows up here when this tab gets focus again.
    let refreshOnFocus = null;
    const { value } = await Swal.fire({
      ...swalForm,
      customClass: { ...swalForm.customClass, popup: 'ds-swal__popup ca-quiz-swal' },
      title: 'Select a quiz',
      confirmButtonText: 'Use this quiz',
      input: 'select',
      inputOptions: Object.fromEntries(known.map((q) => [q._id, quizLabel(q)])),
      inputValue: rows.quiz.quizRef || known[0]._id,
      footer: '<a href="/admin/quizzes/create">Create a new quiz in Quiz Management</a>',
      didOpen: () => {
        Swal.getFooter()?.querySelector('a')?.addEventListener('click', (e) => {
          if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return; // the browser's own "open in new tab"
          e.preventDefault();
          Swal.close();
          goCreateQuiz();
        });
        refreshOnFocus = async () => {
          const list = await fetchQuizzes();
          const select = Swal.getInput();
          if (!list || !select) return;
          const added = list.filter((q) => !known.some((k) => k._id === q._id));
          const chosen = added[0]?._id || select.value;
          known = list;
          select.replaceChildren(...list.map((q) => new Option(quizLabel(q), q._id)));
          select.value = list.some((q) => q._id === chosen) ? chosen : list[0]?._id || '';
        };
        window.addEventListener('focus', refreshOnFocus);
      },
      willClose: () => window.removeEventListener('focus', refreshOnFocus),
    });
    if (value) setRow('quiz', { quizRef: value, title: known.find((q) => q._id === value)?.title || 'Quiz' });
  };

  const view = async (row) => {
    const value = rows[row.key];
    if (row.type === 'quiz') { window.open('/admin/quizzes', '_blank', 'noopener'); return; }
    const target = row.type === 'link' ? value.externalUrl : value.fileUrl;
    if (!target) return;
    const tab = window.open('', '_blank');
    try {
      const res = row.type === 'link' ? null : await api.get('/api/v2/lms/admin/courses/files/view-url', { params: { url: target } });
      if (tab) tab.location.href = res?.data?.data?.url || target;
    } catch {
      if (tab) tab.location.href = target;
    }
  };

  const edit = (row) => {
    if (row.type === 'link') return editGame();
    if (row.type === 'quiz') return editQuiz();
    return fileInputs.current[row.key]?.click();
  };

  const onThumb = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png)$/.test(file.type)) return resultDialog('error', 'Use a JPG or PNG image');
    if (file.size > MAX_THUMB_BYTES) return resultDialog('error', 'Image too large', 'The thumbnail must be 2 MB or smaller.');
    setBusy('thumb');
    try {
      const uploaded = await upload(file);
      setThumb({ url: uploaded.fileUrl, preview: URL.createObjectURL(file) });
    } catch (err) {
      resultDialog('error', 'Upload failed', err?.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const leave = async () => {
    if (dirty && !(await confirmDialog({ title: 'Discard this content?', text: 'Your changes have not been saved.', confirmText: 'Discard', cancelText: 'Keep editing', danger: true }))) return;
    navigate(backTo);
  };

  // Saves every item (one request each), in order, each with its Syllabus Tracker number.
  const save = async () => {
    if (!course && !newCourse) return resultDialog('error', `Choose a ${category ? 'subcategory' : 'category'}`, category ? `Pick one of the subcategories of "${where}" on the left.` : 'Pick a category on the left.');
    const payloads = [];
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      const name = itemTitle(i).trim();
      const which = items.length > 1 ? `${name || `Item ${i + 1}`}: ` : '';
      const fail = (heading, text) => {
        setActive(i);
        resultDialog('error', `${which}${heading}`, text);
      };
      if (!name) return fail(`${noun} title is required`);
      if (!it.description.trim()) return fail('Short description is required');
      const contentItems = ROWS.filter((r) => isFilled(r, it.rows[r.key])).map((r) => ({
        type: r.type,
        title: it.rows[r.key].title || r.label,
        fileUrl: it.rows[r.key].fileUrl || undefined,
        externalUrl: it.rows[r.key].externalUrl || undefined,
        quizRef: it.rows[r.key].quizRef || undefined,
        coins: coins(it.rows[r.key].coins),
      }));
      if (!contentItems.length) return fail('Add at least one item', 'Choose a PDF, audio, video, game or quiz.');
      payloads.push({
        title: name,
        description: it.description.trim(),
        thumbnail: it.thumb.url || undefined,
        defaultCoins: coins(it.defaultCoins),
        coinRules: it.coinRules.filter((r) => r.label.trim()).map((r) => ({ label: r.label.trim(), coins: coins(r.coins) })),
        items: contentItems,
        ...(naming ? { sessionNumber: naming.next + i, ...(naming.topic ? { syllabusTopic: naming.topic } : {}) } : {}),
      });
    }
    setBusy('save');
    let target = course;
    if (!target) {
      // The row's course, made now; it stays picked here, so saving again adds to it.
      try {
        const thumbnail = items.find((it) => it.thumb.url)?.thumb.url;
        const res = await api.post('/api/v2/lms/admin/courses', { ...courseForRow(category, subcategoryNode, where), ...(thumbnail ? { thumbnail } : {}) });
        target = res.data?.data;
        if (!target?._id) throw new Error('No course was returned');
        setCourses((list) => [target, ...list]);
        setCourseId(target._id);
      } catch (err) {
        setBusy('');
        return resultDialog('error', `Could not save the ${noun.toLowerCase()}`, err?.response?.data?.error || 'Please try again.');
      }
    }
    let saved = 0;
    // The tracker number isn't shown, so one taken meanwhile (409, e.g. by an archived course's
    // chapter or another admin's save) is replaced by the free one the server names, and the later
    // items move on with it.
    let shift = 0;
    try {
      for (const payload of payloads) {
        if (payload.sessionNumber) payload.sessionNumber += shift;
        try {
          await api.post(`/api/v2/lms/admin/courses/${target._id}/stories`, payload);
        } catch (err) {
          const free = err?.response?.status === 409 ? err.response.data?.next : null;
          if (!payload.sessionNumber || !Number.isInteger(free) || free <= payload.sessionNumber) throw err;
          shift += free - payload.sessionNumber;
          payload.sessionNumber = free;
          await api.post(`/api/v2/lms/admin/courses/${target._id}/stories`, payload);
        }
        saved += 1;
      }
      // A subcategory's own course (made here) goes live with its content, so students see it now.
      let unpublished = '';
      if (target.rowCourse && target.status !== 'published') {
        try {
          await api.put(`/api/v2/lms/admin/courses/${target._id}/publish`);
        } catch (err) {
          unpublished = ` Students can't see it yet: ${err?.response?.data?.errors?.[0] || err?.response?.data?.error || 'publishing failed'}.`;
        }
      }
      const names = payloads.map((payload) => `"${payload.title}"`);
      resultDialog(unpublished ? 'warning' : 'success', `${noun} saved`, `${names.join(', ')} ${names.length > 1 ? 'were' : 'was'} added to ${where}.${unpublished}`);
      // Open the course the items were added to, where they now show in its structure.
      navigate(`/admin/courses/${target._id}/view`);
    } catch (err) {
      // Keep the items that were not saved, numbered on after the saved ones.
      if (saved) {
        setItems((list) => list.slice(saved));
        setActive(0);
        loadCourses().then((r) => setCourses(r.data?.data || [])).catch(() => {});
      }
      const done = saved ? ` ${payloads.slice(0, saved).map((payload) => `"${payload.title}"`).join(', ')} saved; the rest are still here.` : '';
      resultDialog('error', `Could not save the ${noun.toLowerCase()}`, `${err?.response?.data?.error || 'Please try again.'}${done}`);
    } finally {
      setBusy('');
    }
  };

  if (rbacLoading) return <LoadingState message="Loading permissions..." fullScreen />;

  return (
    <div className="cm-page">
      <div className="cm-layout">
        <CategorySidebar
          categories={categories}
          selected={category}
          onSelect={pickCategory}
          subcategoryNodes={nodesFor}
          selectedSubcategory={subcategory}
          onSelectSubcategory={pickSubcategory}
          onAddCategory={async () => { if (await promptAddCategory(categories)) reloadCategories(); }}
        />

        <main className="cm-main">
          <div className="ca-crumbs ca-crumbs--form">
            <button type="button" className="cm-btn cm-btn--ghost cm-btn--sm" onClick={leave}><ArrowLeft size={16} />Back</button>
            {/* One line of text (wrapping between words only when it must), not a column per piece. */}
            <span className="ca-crumbs__trail">
              <span className="ca-crumbs__icon" aria-hidden="true">{categoryIcon(meta)}</span>
              <b className="ca-crumbs__path">{where || 'Choose a category'}</b>
              <span className="ca-crumbs__sep" aria-hidden="true">›</span>
              <b className="ca-crumbs__here">Add New {noun}</b>
            </span>
            {/* For the row picked in the sidebar: the category, or one of its subcategories. */}
            {category && (
              <SubcategoryActions
                label={subcategoryNode?.label || categoryLabel(category)}
                onAddSubcategory={canAddSubcategoryUnder(nodesFor(category), subcategoryNode?.key)
                  ? async () => { if (await promptAddSidebarSubcategory(category, nodesFor(category), subcategoryNode?.key)) reloadSubcategories(); }
                  : null}
              />
            )}
            {isGComprisRow(category, subcategoryNode) && (
              <button type="button" className="cm-btn cm-btn--coins cm-btn--sm" aria-label="GCompris coin settings" title="Coins students earn in GCompris games" onClick={() => setGcomprisOpen(true)}>
                <Coins size={15} /> Coin Settings
              </button>
            )}
          </div>

          <section className="ca-card">
            {!course && !newCourse ? (
              <div className="ca-pick-hint">
                {/* Until the list arrives, not "Pick one of the subcategories". */}
                {coursesLoading && category
                  ? <>Loading…</>
                  : category
                    ? <>Pick one of the subcategories under <b>{where}</b> on the left to add {noun.toLowerCase()} there.</>
                    : <>Choose a category on the left to begin.</>}
              </div>
            ) : (
            <>
            {(naming || savedRows.length > 0) && (
              <div className="ca-items">
                {/* What the row already has (Action Song 1 ...), before the ones being added; each opens its details. */}
                {savedRows.length > 0 && (
                  <span className="ca-items__saved" role="group" aria-label={`Already in ${where}`}>
                    {savedRows.map((saved) => (
                      <span key={saved.key} className="ca-item-tab is-saved">
                        <button type="button" title="Already added: see it" aria-label={`See ${saved.chapter.title || 'saved content'}`} onClick={() => setDetails(saved)}>
                          <Check size={14} aria-hidden="true" />{saved.chapter.title || 'Untitled'}
                        </button>
                      </span>
                    ))}
                  </span>
                )}
                {naming && (
                  <span className="ca-items__tabs" role="tablist" aria-label={`${naming.prefix} items`}>
                    {items.map((it, i) => (
                      <span key={i} className={`ca-item-tab${i === active ? ' is-active' : ''}`}>
                        <button type="button" role="tab" aria-selected={i === active} onClick={() => setActive(i)}>{itemTitle(i) || `Item ${i + 1}`}</button>
                        {items.length > 1 && (
                          <button type="button" className="ca-item-tab__remove" aria-label={`Remove ${itemTitle(i) || `item ${i + 1}`}`} onClick={() => removeItem(i)}><X size={13} /></button>
                        )}
                      </span>
                    ))}
                    <button type="button" className="ca-add" onClick={addItem}><Plus size={15} />Add {naming.prefix}</button>
                  </span>
                )}
              </div>
            )}
            <h3 className="ca-step"><span>1</span>Basic Details</h3>
            <div className="ca-basic">
              <label className="ca-field">
                <span>{noun} Title <em>*</em></span>
                <input value={itemTitle(active)} maxLength={200} onChange={(e) => setTitle(e.target.value)} placeholder={`Enter ${noun.toLowerCase()} title`} />
              </label>
              <label className="ca-field">
                <span>Short Description <em>*</em></span>
                <textarea value={description} maxLength={DESC_MAX} onChange={(e) => setDescription(e.target.value)} placeholder={`Enter short description of the ${noun.toLowerCase()}...`} />
                <small className="ca-count">{description.length}/{DESC_MAX}</small>
              </label>
              <div className="ca-field">
                <span>Thumbnail Image <small>(Optional)</small></span>
                <button type="button" className="ca-thumb" onClick={() => thumbInput.current?.click()} disabled={busy === 'thumb'}>
                  {thumb.preview ? <img src={thumb.preview} alt="Thumbnail preview" /> : (
                    <>
                      <ImagePlus size={26} />
                      <b>{busy === 'thumb' ? 'Uploading…' : 'Upload Image'}</b>
                      <small>JPG, PNG (Max 2 MB)</small>
                    </>
                  )}
                </button>
                {thumb.preview && <button type="button" className="cm-link" onClick={() => setThumb({ url: '', preview: '' })}>Remove image</button>}
                <input ref={thumbInput} type="file" accept="image/jpeg,image/png" hidden onChange={(e) => { onThumb(e.target.files?.[0]); e.target.value = ''; }} />
              </div>
            </div>

            <div className="ca-step ca-step--bar">
              <h3><span>2</span>Content &amp; Activities</h3>
              <label className="ca-default-coins">
                <span aria-hidden="true">🪙</span> Default Coins (per {noun.toLowerCase()})
                <span className="ca-coin-input"><input type="number" min="0" value={defaultCoins || ''} placeholder="0" onChange={(e) => setDefaultCoins(e.target.value)} /><i>₹</i></span>
              </label>
            </div>

            <div className="cm-table-wrap">
              <table className="cm-table ca-table">
                <thead>
                  <tr><th>Sr. No.</th><th>Content Type</th><th>Source / Action</th><th>ISF Coins</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {ROWS.map((row, i) => {
                    const value = rows[row.key];
                    const filled = isFilled(row, value);
                    return (
                      <tr key={row.key}>
                        <td>{i + 1}</td>
                        <td><span className="ca-type"><i aria-hidden="true">{row.icon}</i>{row.label}</span></td>
                        <td>
                          {row.libraryType ? (
                            <>
                              <SearchableSelect searchPlaceholder="Search file..." className="ca-source" value={value.fileUrl} onChange={(e) => onSource(row, e.target.value)} disabled={busy === row.key}>
                                <option value="">{busy === row.key ? 'Uploading…' : 'Select from MKP'}</option>
                                {(mkp[row.libraryType] || []).length > 0 ? mkpGroups(mkp[row.libraryType]).map(([group, files]) => (
                                  <optgroup key={group} label={`MKP – ${group}`}>
                                    {files.map((f) => <option key={f.id} value={f.fileUrl}>{mkpLabel(f)}</option>)}
                                  </optgroup>
                                )) : (
                                  <optgroup label="MKP">
                                    <option value="__mkp_note__" disabled>{mkpNote(mkpStatus, row.libraryType)}</option>
                                  </optgroup>
                                )}
                                {(library[row.libraryType] || []).length > 0 && (
                                  <optgroup label="Content Library">
                                    {library[row.libraryType].map((f) => <option key={f._id || f.id || f.fileUrl} value={f.fileUrl}>{f.fileName}</option>)}
                                  </optgroup>
                                )}
                                {value.fileUrl && ![...(mkp[row.libraryType] || []), ...(library[row.libraryType] || [])].some((f) => f.fileUrl === value.fileUrl) && <option value={value.fileUrl}>{value.title || 'Selected file'}</option>}
                                <option value={UPLOAD_NEW}>⬆ Upload new file…</option>
                              </SearchableSelect>
                              <input ref={(el) => { fileInputs.current[row.key] = el; }} type="file" accept={row.accept} hidden onChange={(e) => { onPickFile(row, e.target.files?.[0]); e.target.value = ''; }} />
                            </>
                          ) : row.type === 'link' ? (
                            filled ? <span className="ca-picked" title={value.externalUrl}>🎮 {value.title}</span>
                              : <button type="button" className="ca-add" onClick={editGame}><Plus size={15} />Add Game</button>
                          ) : (
                            filled ? <span className="ca-picked">❓ {value.title}</span>
                              : <button type="button" className="ca-add" onClick={editQuiz}><Plus size={15} />Create Quiz</button>
                          )}
                        </td>
                        <td>
                          <span className="ca-coin-input"><i>₹</i><input type="number" min="0" value={value.coins || ''} placeholder="0" onChange={(e) => setRow(row.key, { coins: e.target.value })} aria-label={`${row.label} coins`} /></span>
                        </td>
                        <td>
                          <div className="ca-row-actions">
                            <button type="button" className="ca-act ca-act--view" disabled={!filled} onClick={() => view(row)}><Eye size={15} />View</button>
                            <button type="button" className="ca-act ca-act--edit" onClick={() => edit(row)}><Pencil size={15} />Edit</button>
                            <button type="button" className="ca-act ca-act--delete" disabled={!filled && !coins(value.coins)} onClick={() => setRow(row.key, emptyRow())}><Trash2 size={15} />Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {coinRules.map((rule, i) => (
                    <tr key={`rule-${i}`} className="ca-rule">
                      <td>{ROWS.length + i + 1}</td>
                      <td><span className="ca-type"><i aria-hidden="true">➕</i>Coin rule</span></td>
                      <td><input className="ca-source" value={rule.label} maxLength={100} placeholder="e.g. Expression, Half side" onChange={(e) => setCoinRules((all) => all.map((r, j) => (j === i ? { ...r, label: e.target.value } : r)))} /></td>
                      <td><span className="ca-coin-input"><i>₹</i><input type="number" min="0" value={rule.coins || ''} placeholder="0" onChange={(e) => setCoinRules((all) => all.map((r, j) => (j === i ? { ...r, coins: e.target.value } : r)))} aria-label="Rule coins" /></span></td>
                      <td><div className="ca-row-actions"><button type="button" className="ca-act ca-act--delete" onClick={() => setCoinRules((all) => all.filter((_, j) => j !== i))}><X size={15} />Remove</button></div></td>
                    </tr>
                  ))}
                  <tr className="ca-total">
                    <td>{ROWS.length + coinRules.length + 1}</td>
                    <td><span className="ca-type"><i aria-hidden="true">⭐</i>Coins (Total for {noun})</span></td>
                    <td><span className="ca-auto">Auto-calculated (default + items + coin rules)</span></td>
                    <td><span className="ca-coin-input is-total"><i>₹</i><input value={totalCoins} readOnly aria-label="Total coins" /></span></td>
                    <td>—</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="ca-rules-bar">
              <button type="button" className="ca-add" onClick={() => setCoinRules((all) => [...all, { label: '', coins: 0 }])}><Plus size={15} />Add coin rule</button>
              <small>Give coins for specific achievements, e.g. Expression – 10 coins, Half side – 15 coins.</small>
            </div>

            <footer className="ca-foot">
              <button type="button" className="cm-btn cm-btn--purple" onClick={save} disabled={busy === 'save'}><Save size={17} />{busy === 'save' ? 'Saving…' : `Save ${noun}`}</button>
              <button type="button" className="cm-btn cm-btn--red" onClick={leave} disabled={busy === 'save'}>Cancel</button>
            </footer>
            </>
            )}
          </section>
        </main>
      </div>

      <GComprisRewardModal isOpen={gcomprisOpen} onClose={() => setGcomprisOpen(false)} />
      {details && (
        <ContentDetailsDialog
          row={details}
          where={where}
          onClose={() => setDetails(null)}
          onChanged={() => loadCourses().then((r) => setCourses(r.data?.data || [])).catch(() => {})}
        />
      )}
    </div>
  );
}
